import { useEffect, useRef } from 'react';

import { useTrackEvent, type CheckoutSource } from '../../lib/trackingEvents';

// Once per form mount. A Stripe success return is the tail of a visit already counted, not a new one, so it is left
// out; a cancelled return is a retry and counts.
export function useTrackCheckoutLoaded({
    source,
    payment,
}: {
    source: CheckoutSource;
    payment?: 'success' | 'cancelled';
}): void {
    const track = useTrackEvent();
    const tracked = useRef(false);
    // Latest values without re-running: only the mount is the event.
    const latest = useRef({ source, payment });
    latest.current = { source, payment };

    useEffect(() => {
        if (tracked.current || latest.current.payment === 'success') {
            return;
        }
        tracked.current = true;
        track('Checkout Loaded', { checkoutSource: latest.current.source });
    }, [track]);
}

// Only a completion seen in this visit counts: the form was on screen before the order turned confirmed, or Stripe
// sent the partner back with a success. Reopening an already-confirmed link later is neither.
export function useTrackCheckoutCompleted({
    source,
    hasOrder,
    isConfirmed,
    payment,
}: {
    source: CheckoutSource;
    hasOrder: boolean;
    isConfirmed: boolean;
    payment?: 'success' | 'cancelled';
}): void {
    const track = useTrackEvent();
    const sawForm = useRef(false);
    const tracked = useRef(false);

    useEffect(() => {
        if (!isConfirmed) {
            // Without an order nothing was on screen to complete (an expired link, a plan not yet chosen).
            sawForm.current ||= hasOrder;
            return;
        }
        if (tracked.current || !hasOrder || (!sawForm.current && payment !== 'success')) {
            return;
        }
        tracked.current = true;
        track('Checkout Completed', { checkoutSource: source });
    }, [isConfirmed, hasOrder, payment, source, track]);
}
